import { useCallback } from 'react';
import { useAction, useMutation, useQuery } from 'convex/react';
import type { FunctionArgs, FunctionReference, FunctionReturnType } from 'convex/server';
import { useOptionalAuth } from '../contexts/AuthContext';

/**
 * Convex hookok, amelyek automatikusan átadják a bejelentkezési munkamenet-tokent.
 *
 * Minden olyan Convex függvényhez ezeket kell használni, amely `sessionToken`
 * argumentumot fogad (a szerver ebből tudja, ki hív — soha nem a kliens által
 * küldött userId-ból). A hívó oldalon a `sessionToken`-t nem kell megadni.
 */

type WithoutToken<F extends FunctionReference<any>> = Omit<FunctionArgs<F>, 'sessionToken'>;
/** Ha minden argumentum opcionális, a hívás argumentum nélkül is mehet. */
type CallArgs<F extends FunctionReference<any>> = {} extends WithoutToken<F>
  ? [args?: WithoutToken<F>]
  : [args: WithoutToken<F>];

/** useQuery + token. Token nélkül (kijelentkezve) a lekérdezés kimarad ("skip"). */
export function useSessionQuery<F extends FunctionReference<'query'>>(
  query: F,
  args: WithoutToken<F> | 'skip'
): FunctionReturnType<F> | undefined {
  const sessionToken = useOptionalAuth()?.sessionToken ?? null;
  const finalArgs = args === 'skip' || !sessionToken ? 'skip' : ({ ...args, sessionToken } as FunctionArgs<F>);
  return useQuery(query, finalArgs as any);
}

/** useMutation + token. */
export function useSessionMutation<F extends FunctionReference<'mutation'>>(mutation: F) {
  const sessionToken = useOptionalAuth()?.sessionToken ?? null;
  const run = useMutation(mutation);
  return useCallback(
    (...[args]: CallArgs<F>): Promise<FunctionReturnType<F>> =>
      run({ ...(args ?? {}), sessionToken: sessionToken ?? undefined } as FunctionArgs<F>),
    [run, sessionToken]
  );
}

/** useAction + token. */
export function useSessionAction<F extends FunctionReference<'action'>>(action: F) {
  const sessionToken = useOptionalAuth()?.sessionToken ?? null;
  const run = useAction(action);
  return useCallback(
    (...[args]: CallArgs<F>): Promise<FunctionReturnType<F>> =>
      run({ ...(args ?? {}), sessionToken: sessionToken ?? undefined } as FunctionArgs<F>),
    [run, sessionToken]
  );
}
